import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import StartAdornment from '@mui/material/InputAdornment';
import ListItem from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import { StyledInput } from 'components/form/TextInput';
import PropTypes from 'prop-types';
import * as React from 'react';
import Button from './Button/Button';
import {
  CheckboxCheckedIcon,
  CheckboxIcon,
  ChevronDownIcon,
  SearchIcon,
} from './icons';

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.background.paper5,
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      '&:not(:last-child)': {
        marginBottom: 4,
      },
      [`&.${autocompleteClasses.focused}[aria-selected="true"]`]: {
        backgroundColor: theme.palette.action.highlight,
      },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}));

function PopperComponent(props) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

PopperComponent.propTypes = {
  anchorEl: PropTypes.any,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({ theme, width }) => ({
  border: `1px solid ${theme.palette.dividerVariant}`,
  boxShadow: theme.shadows[5],
  borderRadius: 8,
  width,
  zIndex: theme.zIndex.modal,
  backgroundColor: theme.palette.background.paper5,
  '& .MuiAutocomplete-popper': {
    width: '100% !important',
  },
  marginTop: '8px !important',
}));

export default function Picker({
  options,
  value,
  onChange,
  children,
  placeholder,
  title,
  width = 300,
  multiple = true,
  placement = 'bottom-end',
  renderFooter,
  icon,
  label,
  size,
  ...rest
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingValue, setPendingValue] = React.useState([]);
  const theme = useTheme();

  const onTriggerClick = (event) => {
    setPendingValue(value);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    onChange(pendingValue);
    onClose();
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const clearAll = () => {
    setPendingValue([]);
  };

  const open = Boolean(anchorEl);
  const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

  const renderChildren = () => {
    if (React.isValidElement(children)) {
      return React.cloneElement(children, { onClick: onTriggerClick });
    }

    return (
      <Button
        rounded
        size={size}
        onClick={onTriggerClick}
        color={hasValue ? 'primary' : 'secondary'}
        startIcon={icon}
        endIcon={
          multiple && value?.length ? (
            <Badge
              sx={{
                mr: 1.5,
                ml: 1,
              }}
              color="error"
              size="small"
              badgeContent={value.length}
            />
          ) : (
            <ChevronDownIcon />
          )
        }
        variant={hasValue ? 'tonal' : 'outlined'}
      >
        {label}
      </Button>
    );
  };

  let footerComponent;
  if (renderFooter) {
    footerComponent = renderFooter({ onClose, clearAll });
  } else if (multiple) {
    footerComponent = (
      <Box sx={{ pb: 1 }}>
        <Divider />
        <Stack px={1} pt={1} direction="row" justifyContent="space-between">
          <Button
            size="mini"
            variant="outlined"
            color="secondary"
            onClick={() => {
              setPendingValue(options);
            }}
          >
            Select All
          </Button>
          <Button
            size="mini"
            variant="outlined"
            color="secondary"
            onClick={clearAll}
          >
            Clear All
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <React.Fragment>
      {renderChildren()}
      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        width={width}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            {title && (
              <Box
                sx={{
                  borderBottom: `1px solid ${theme.palette.dividerVariant}`,
                  padding: '8px 10px',
                  fontWeight: 600,
                }}
              >
                {title}
              </Box>
            )}
            <Autocomplete
              open
              slots={{
                paper: Box,
              }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: { p: 1 },
                },
              }}
              multiple={multiple}
              onClose={(event, reason) => {
                if (reason === 'escape') {
                  handleClose();
                }
              }}
              value={multiple ? pendingValue : value}
              onChange={(event, newValue, reason) => {
                if (
                  event.type === 'keydown' &&
                  event.key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return;
                } else if (reason === 'clear') {
                  return;
                }
                setPendingValue(newValue);
                if (!multiple) {
                  handleClose();
                  onChange(newValue);
                }
              }}
              disableCloseOnSelect={multiple}
              PopperComponent={PopperComponent}
              renderTags={() => null}
              renderOption={(props, option, { selected }) =>
                option.render ? (
                  <li {...props} key={option.value}>
                    {option.render(option)}
                  </li>
                ) : (
                  <ListItem
                    dense
                    {...props}
                    key={option.value}
                    sx={{ borderRadius: '6px' }}
                  >
                    {multiple && (
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Checkbox
                          checked={selected}
                          sx={{ p: 0 }}
                          icon={<CheckboxIcon />}
                          checkedIcon={<CheckboxCheckedIcon />}
                        />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      slotProps={{
                        primary: { sx: { fontWeight: 500 } },
                      }}
                      primary={option.label}
                      secondary={option.description}
                    />
                  </ListItem>
                )
              }
              options={options}
              getOptionLabel={(option) => option.label || ''}
              renderInput={(params) => (
                <Box p={1} pb={0}>
                  <StyledInput
                    fullWidth
                    startAdornment={
                      <StartAdornment position="start">
                        <SearchIcon />
                      </StartAdornment>
                    }
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    placeholder={placeholder}
                  />
                </Box>
              )}
              {...rest}
            />
            {footerComponent}
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </React.Fragment>
  );
}

Picker.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.number,
  multiple: PropTypes.bool,
  placement: PropTypes.string,
  renderFooter: PropTypes.func,
  icon: PropTypes.node,
};
